import { HttpMethods, makeRequest } from '../helpers/http.helper';
import { OrganizationRequest, UpdateOrganizationRequest, OrganizationQueryParams, UpdateOrganizationPilotRequest, OrganizationWasteType } from '../types/organization.type';
import { unwrapList, unwrapOne } from '../utils/api-envelope.helpers';

export const createOrganization = (organization: OrganizationRequest) => {
    return makeRequest({
        url: "/organization",
        method: HttpMethods.POST,
        data: organization,
    });
};

export const getOrganizations = (params?: OrganizationQueryParams) => {
    return makeRequest({
        url: "/organization",
        method: HttpMethods.GET,
        params: params || {},
    });
};

export const getOrganization = (organizationId: string) => {
    return makeRequest({
        url: `/organization/${organizationId}`,
        method: HttpMethods.GET,
    });
};

export const updateOrganization = (organizationId: string, organization: UpdateOrganizationRequest) => {
    return makeRequest({
        url: `/organization/${organizationId}`,
        method: HttpMethods.PUT,
        data: organization,
    });
};

/** The waste types this organization handles -- what its pickers offer.
 *  `includeUsage` adds per-type counts of the organization's pickup orders,
 *  containers and offers, for the screen where waste types are removed. */
export const getOrganizationWasteTypes = async (
    organizationId: string,
    options: { includeUsage?: boolean } = {},
): Promise<OrganizationWasteType[]> =>
    unwrapList<OrganizationWasteType>(
        await makeRequest({
            url: `/organization/${organizationId}/waste-types`,
            method: HttpMethods.GET,
            params: options.includeUsage ? { include_usage: true } : {},
        }),
    );

/** Replaces the whole selection. Deselecting a waste type only stops it being
 *  offered for new work; existing records keep it. The server refuses (409) to
 *  drop a type a supply target or an open pre-order still counts on. */
export const replaceOrganizationWasteTypes = async (
    organizationId: string,
    materialIds: string[],
): Promise<OrganizationWasteType[]> =>
    unwrapList<OrganizationWasteType>(
        await makeRequest({
            url: `/organization/${organizationId}/waste-types`,
            method: HttpMethods.PUT,
            data: { material_ids: materialIds },
        }),
    );

/** Takes one catalog waste type into the organization's basket. 409 when it is already there. */
export const addOrganizationWasteType = async (
    organizationId: string,
    materialId: string,
    clientCustomId?: string | null,
): Promise<OrganizationWasteType | null> =>
    unwrapOne<OrganizationWasteType>(
        await makeRequest({
            url: `/organization/${organizationId}/waste-types`,
            method: HttpMethods.POST,
            data: { material_id: materialId, client_custom_id: clientCustomId ?? null },
        }),
    );

/** Changes only what the organization calls the waste type; the type itself is the catalog's. */
export const updateOrganizationWasteType = async (
    organizationId: string,
    materialId: string,
    clientCustomId: string | null,
): Promise<OrganizationWasteType | null> =>
    unwrapOne<OrganizationWasteType>(
        await makeRequest({
            url: `/organization/${organizationId}/waste-types/${materialId}`,
            method: HttpMethods.PATCH,
            data: { client_custom_id: clientCustomId },
        }),
    );

/** Stops offering the waste type to this organization. Records already using it are untouched. */
export const removeOrganizationWasteType = async (organizationId: string, materialId: string): Promise<void> => {
    await makeRequest({
        url: `/organization/${organizationId}/waste-types/${materialId}`,
        method: HttpMethods.DELETE,
    });
};

export const updateOrganizationPilot = (
    organizationId: string,
    pilot: UpdateOrganizationPilotRequest,
) => {
    return makeRequest({
        url: `/organization/${organizationId}/pilot`,
        method: HttpMethods.PATCH,
        data: pilot,
    });
};

/** Platform admins only: mark a recycler as the operator's own warehouse. */
export const updateOrganizationInternalHub = (organizationId: string, isInternalHub: boolean) => {
    return makeRequest({
        url: `/organization/${organizationId}/internal-hub`,
        method: HttpMethods.PATCH,
        data: { is_internal_hub: isInternalHub },
    });
};

export const deleteOrganization = (organizationId: string) => {
    return makeRequest({
        url: `/organization/${organizationId}`,
        method: HttpMethods.DELETE,
    });
};
